/**
 * <s-scroll-row> — Horizontal scroll-snap row paged by prev/next arrows.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --prev — Scroll back by roughly one visible page.
 *   --next — Scroll forward by roughly one visible page.
 *
 * Required child markup:
 *   [data-scroll-track] — The horizontally scrollable list.
 *   [data-scroll-prev]  — Previous button; disabled at the start.
 *   [data-scroll-next]  — Next button; disabled at the end.
 */
class SScrollRow extends HTMLElement {
  #ac;
  #track;
  #frame = 0;
  #resizeObserver;

  connectedCallback() {
    this.#track = this.querySelector('[data-scroll-track]');
    if (!this.#track) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track.addEventListener('scroll', this.#queueUpdate, { ...opts, passive: true });
    window.addEventListener('resize', this.#queueUpdate, { ...opts, passive: true });

    // Rows inside hidden tabs measure zero until shown; re-check whenever the track's size changes.
    this.#resizeObserver = new ResizeObserver(this.#queueUpdate);
    this.#resizeObserver.observe(this.#track);

    this.#update();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #handleCommand = (event) => {
    if (event.command !== '--prev' && event.command !== '--next') return;

    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    const forward = event.command === '--next' ? 1 : -1;
    const distance = this.#track.clientWidth * 0.9 * forward * (rtl ? -1 : 1);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#track.scrollBy({ left: distance, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  #queueUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const offset = Math.abs(this.#track.scrollLeft);
    const max = this.#track.scrollWidth - this.#track.clientWidth;
    const prev = this.querySelector('[data-scroll-prev]');
    const next = this.querySelector('[data-scroll-next]');
    if (prev) prev.disabled = offset <= 2;
    if (next) next.disabled = offset >= max - 2;
  }
}

if (!customElements.get('s-scroll-row')) {
  customElements.define('s-scroll-row', SScrollRow);
}
